import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Alert, CircularProgress,
  IconButton, TextField, LinearProgress, Tooltip, Stack, Checkbox
} from '@mui/material';
import { CloudUpload, Warning, Refresh, CheckCircle, Close as CloseIcon, CloudSync, DeleteOutline, FactCheck } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import axios from 'axios';
import config from '../../config/config';
import { useSession } from '../../components/hooks/useSession';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';

const apiUrl = config.apiBaseUrl;

// Normaliza cualquier valor numérico a centavos enteros para comparación exacta
const toCentavos = (val) => {
  if (val === null || val === undefined || val === '') return null;
  // xlsx devuelve celdas numéricas como number — usar directo
  if (typeof val === 'number') {
    if (isNaN(val)) return null;
    return Math.round(val * 100);
  }
  // Si es string, remover texto de divisas (USD, MXN, MXP, EUR) y caracteres alfábeticos
  let str = String(val)
    .toUpperCase()
    .replace(/USD|MXN|MXP|EUR/g, '')
    .replace(/[A-Z]/g, '')
    .replace(/\s/g, '')
    .replace(/[$]/g, '')
    .trim();

  if (!str || str === '-' || str === 'N/A') return null;

  const lastComma = str.lastIndexOf(',');
  const lastPeriod = str.lastIndexOf('.');

  let normalized;
  if (lastComma !== -1 && lastPeriod !== -1) {
    if (lastComma > lastPeriod) {
      // Formato europeo: 1.234,56 → quitar puntos, coma → punto
      normalized = str.replace(/\./g, '').replace(',', '.');
    } else {
      // Formato americano: 1,234.56 → quitar comas
      normalized = str.replace(/,/g, '');
    }
  } else if (lastComma !== -1) {
    // Solo coma: si está seguida de 2 dígitos exactos → decimal
    normalized = str.slice(lastComma + 1).length <= 2
      ? str.replace(',', '.')
      : str.replace(/,/g, '');
  } else {
    // Solo punto o nada: dejar tal cual
    normalized = str;
  }
  const num = parseFloat(normalized);
  return isNaN(num) ? null : Math.round(num * 100);
};

// Formatea centavos a string de moneda
const formatMonto = (centavos) => {
  if (centavos === null || centavos === undefined) return null;
  return (centavos / 100).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function ControlExhibits() {
  const { loggedInUsername } = useSession();

  const [loading, setLoading] = useState(false);

  // Lista completa de solicitudes de BD: { AUTSOLPAGOID, FOLIO_OC, MONTO_SOLICITADO, EXHIBICION }
  const [solicitudesBD, setSolicitudesBD] = useState([]);
  const [stats, setStats] = useState({ total: 0, conExhibicion: 0 });

  // Modal de conciliación
  const [modalOpen, setModalOpen] = useState(false);
  const [registrosNuevos, setRegistrosNuevos] = useState([]);
  const [conflictos, setConflictos] = useState([]);
  const [resumen, setResumen] = useState({ leidas: 0, ignoradas: 0, sinMontoDetectado: false, columnasMonto: '' });

  // Modal de Auditoría de Discrepancias
  const [modalAuditoriaOpen, setModalAuditoriaOpen] = useState(false);
  const [discrepancias, setDiscrepancias] = useState([]);
  const [auditando, setAuditando] = useState(false);
  const [guardandoAuditoria, setGuardandoAuditoria] = useState(false);

  // Guardado
  const [guardando, setGuardando] = useState(false);
  const [syncingMongo, setSyncingMongo] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [progresoMsg, setProgresoMsg] = useState('');

  // UI
  const [alertaMensaje, setAlertaMensaje] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // ─── 1. Cargar solicitudes de BD ──────────────────────────────────────────────
  const fetchSolicitudesBD = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(`${apiUrl}/getExhibicionesActuales`, { idEmpresa: 1 });
      const rows = resp.data?.data || [];
      setSolicitudesBD(rows);
      setStats({
        total: rows.length,
        conExhibicion: rows.filter(r => r.EXHIBICION && String(r.EXHIBICION).trim() !== '').length
      });
    } catch (err) {
      console.error('Error al cargar solicitudes:', err);
      setAlertaMensaje({ type: 'error', text: 'Error al cargar datos desde la base de datos.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSolicitudesBD(); }, []);

  // ─── 1.1 Cargar Exhibits desde MongoDB (EA) ──────────────────────────────────
  const handleSyncMongo = async () => {
    setSyncingMongo(true);
    try {
      const resp = await axios.post(`${apiUrl}/getExhibitsFromMongo`);
      const rawRows = resp.data?.data || [];
      if (!rawRows.length) {
        setAlertaMensaje({ type: 'warning', text: 'No se encontraron exhibiciones válidas en MongoDB (EA).' });
        return;
      }
      procesarExcel(rawRows, 'mongo');
    } catch (err) {
      console.error('Error al consultar MongoDB:', err);
      setAlertaMensaje({
        type: 'error',
        text: `Error al conectar con MongoDB (EA): ${err.response?.data?.mensaje || err.message}`
      });
    } finally {
      setSyncingMongo(false);
    }
  };

  // ─── 1.2 Auditar Discrepancias entre BD y MongoDB EA ─────────────────────────
  const handleAuditarDiscrepancias = async () => {
    setAuditando(true);
    try {
      const resp = await axios.post(`${apiUrl}/getExhibitsFromMongo`);
      const rawRows = resp.data?.data || [];
      if (!rawRows.length) {
        setAlertaMensaje({ type: 'warning', text: 'No se encontraron exhibiciones en MongoDB (EA) para comparar.' });
        return;
      }

      const normExh = (str) => String(str || '').trim().replace(/^Solicitud\s+/i, '').trim().toUpperCase();
      const normOc = (str) => String(str || '').trim().replace(/^0+/, '');

      const eaMapByExh = new Map();
      const eaMapByOcAndExh = new Map();

      rawRows.forEach(row => {
        const exh = normExh(row.EXHIBICION || row.EXHIBIT);
        const oc = normOc(row.OC || row.FOLIO_OC);
        const montoCentavos = toCentavos(row.IMPORTE || row.MONTO);
        if (!exh) return;

        const info = {
          exhibicion: exh,
          oc,
          montoCentavos,
          montoRaw: row.IMPORTE || row.MONTO,
          proveedor: row.PROVEEDOR || '',
          descripcion: row.DESCRIPCION || '',
          fecha: row.FECHA || null
        };

        eaMapByExh.set(exh, info);
        if (oc) {
          eaMapByOcAndExh.set(`${oc}__${exh}`, info);
        }
      });

      const listaDiscrepancias = [];
      const conExhibicion = solicitudesBD.filter(r => r.EXHIBICION && String(r.EXHIBICION).trim() !== '');

      conExhibicion.forEach(sol => {
        const rawExhibicionBD = String(sol.EXHIBICION || '').trim();
        const ocBD = normOc(sol.FOLIO_OC);
        const montoBDCentavos = toCentavos(sol.MONTO_SOLICITADO);

        // Si la exhibición guardada en BD es múltiple (separada por comas, ej: "Solicitud A, Solicitud B")
        const partesExh = rawExhibicionBD.split(',').map(s => normExh(s)).filter(Boolean);

        let motivo = '';
        let tipo = '';
        let eaMontoTotalCentavos = 0;
        let eaInfoPrincipal = null;
        let todasEncontradas = true;

        for (const parte of partesExh) {
          let found = eaMapByOcAndExh.get(`${ocBD}__${parte}`);
          if (!found) {
            found = eaMapByExh.get(parte);
            if (found && found.oc && found.oc !== ocBD) {
              motivo = `OC incompatible en EA (EA tiene OC ${found.oc || 'S/N'} vs BD ${ocBD})`;
              tipo = 'oc_incompatible';
            }
          }
          if (found) {
            if (!eaInfoPrincipal) eaInfoPrincipal = found;
            eaMontoTotalCentavos += (found.montoCentavos || 0);
          } else {
            todasEncontradas = false;
          }
        }

        if (!todasEncontradas && partesExh.length > 0) {
          motivo = 'El exhibit no existe en el catálogo de EA';
          tipo = 'no_existe_ea';
        } else if (todasEncontradas && eaMontoTotalCentavos > 0) {
          const diffCentavos = montoBDCentavos !== null
            ? Math.abs(montoBDCentavos - eaMontoTotalCentavos)
            : 0;

          // Tolerancia:
          // 1. Centavos normales: hasta $1.00 (100 centavos)
          // 2. Margen de tipo de cambio / redondeo: menor entre $500 pesos o el 1.5% del monto
          const margenToleranciaCentavos = Math.max(100, Math.min(50000, Math.round((montoBDCentavos || 0) * 0.015)));

          if (diffCentavos > margenToleranciaCentavos) {
            const diffPesos = ((montoBDCentavos || 0) - eaMontoTotalCentavos) / 100;
            motivo = motivo ? `${motivo} | Diferencia: $${Math.abs(diffPesos).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
              : `Diferencia de monto (Dif: ${diffPesos > 0 ? '+' : ''}$${diffPesos.toLocaleString('es-MX', { minimumFractionDigits: 2 })})`;
            tipo = 'monto_diferente';
          }
        }

        if (motivo) {
          const diffPesos = todasEncontradas && montoBDCentavos !== null && eaMontoTotalCentavos > 0
            ? (montoBDCentavos - eaMontoTotalCentavos) / 100
            : null;

          listaDiscrepancias.push({
            autsolpagoid: sol.AUTSOLPAGOID,
            oc: sol.FOLIO_OC,
            exhibitActual: sol.EXHIBICION,
            montoBD: sol.MONTO_SOLICITADO,
            montoBDCentavos,
            montoEA: todasEncontradas && eaMontoTotalCentavos > 0 ? eaMontoTotalCentavos / 100 : 'N/D',
            diffPesos,
            ocEA: eaInfoPrincipal ? eaInfoPrincipal.oc : null,
            motivo,
            tipo,
            seleccionado: true
          });
        }
      });

      if (listaDiscrepancias.length === 0) {
        setAlertaMensaje({
          type: 'success',
          text: `✅ ¡Auditoría limpia! Se compararon las ${conExhibicion.length} solicitudes con exhibit asignado y todas coinciden exactamente en OC y monto con MongoDB (EA).`
        });
      } else {
        setDiscrepancias(listaDiscrepancias);
        setModalAuditoriaOpen(true);
      }
    } catch (err) {
      console.error('Error al auditar discrepancias:', err);
      setAlertaMensaje({
        type: 'error',
        text: `Error al auditar discrepancias con EA: ${err.response?.data?.mensaje || err.message}`
      });
    } finally {
      setAuditando(false);
    }
  };

  const handleToggleSeleccionDiscrepancia = (autsolpagoid) => {
    setDiscrepancias(prev => prev.map(d =>
      d.autsolpagoid === autsolpagoid ? { ...d, seleccionado: !d.seleccionado } : d
    ));
  };

  const handleToggleSeleccionarTodasDiscrepancias = (checked) => {
    setDiscrepancias(prev => prev.map(d => ({ ...d, seleccionado: checked })));
  };

  const handleDesvincularDiscrepanciaIndividual = async (item) => {
    const res = await handleOpenSweetAlert(
      `¿Deseas desvincular la exhibición "${item.exhibitActual}" de la solicitud #${item.autsolpagoid} (OC: ${item.oc})?`,
      'warning',
      {
        showCancelButton: true,
        confirmButtonText: 'Sí, desvincular',
        cancelButtonText: 'Cancelar'
      }
    );
    if (!res?.isConfirmed) return;

    setGuardandoAuditoria(true);
    try {
      await axios.post(`${apiUrl}/actualizarExhibitsBatch`, {
        idEmpresa: 1,
        usuario: loggedInUsername || 'Sistema',
        items: [{
          autsolpagoid: item.autsolpagoid,
          exhibicionNueva: '',
          exhibicionAnterior: item.exhibitActual
        }]
      });
      setDiscrepancias(prev => prev.filter(d => d.autsolpagoid !== item.autsolpagoid));
      handleOpenSweetAlert(
        `Se desvinculó la exhibición "${item.exhibitActual}" de la solicitud #${item.autsolpagoid} correctamente.`,
        'success'
      );
      fetchSolicitudesBD();
    } catch (err) {
      console.error('Error al desvincular discrepancia:', err);
      handleOpenSweetAlert(`Error al desvincular: ${err.response?.data?.mensaje || err.message}`, 'error');
    } finally {
      setGuardandoAuditoria(false);
    }
  };

  const handleDesvincularDiscrepanciasMasivo = async () => {
    const paraDesvincular = discrepancias.filter(d => d.seleccionado);
    if (!paraDesvincular.length) {
      handleOpenSweetAlert('No seleccionaste ninguna solicitud para desvincular.', 'warning');
      return;
    }

    const res = await handleOpenSweetAlert(
      `¿Estás seguro de que deseas desvincular ${paraDesvincular.length} exhibición(es) seleccionada(s)? Esta acción las dejará sin exhibit en la base de datos.`,
      'warning',
      {
        showCancelButton: true,
        confirmButtonText: 'Sí, desvincular todas',
        cancelButtonText: 'Cancelar'
      }
    );
    if (!res?.isConfirmed) return;

    setGuardandoAuditoria(true);
    try {
      const items = paraDesvincular.map(d => ({
        autsolpagoid: d.autsolpagoid,
        exhibicionNueva: '',
        exhibicionAnterior: d.exhibitActual
      }));

      await axios.post(`${apiUrl}/actualizarExhibitsBatch`, {
        idEmpresa: 1,
        usuario: loggedInUsername || 'Sistema',
        items
      });

      const idsRemovidos = new Set(paraDesvincular.map(d => d.autsolpagoid));
      setDiscrepancias(prev => prev.filter(d => !idsRemovidos.has(d.autsolpagoid)));
      setAlertaMensaje({
        type: 'success',
        text: `✅ Se desvincularon ${paraDesvincular.length} exhibición(es) con discrepancias correctamente.`
      });
      fetchSolicitudesBD();
      if (discrepancias.length === paraDesvincular.length) {
        setModalAuditoriaOpen(false);
      }
    } catch (err) {
      console.error('Error al desvincular en masa:', err);
      setAlertaMensaje({
        type: 'error',
        text: `Error al desvincular discrepancias: ${err.response?.data?.mensaje || err.message}`
      });
    } finally {
      setGuardandoAuditoria(false);
    }
  };

  // ─── 2. Carga del Excel ────────────────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        // Detectar fila de encabezado (busca fila con EXHIBI o (OC y FECHA))
        let headerRow = 0;
        for (let i = 0; i < Math.min(10, matrix.length); i++) {
          const r = matrix[i].join(' ').toUpperCase();
          if (r.includes('EXHIBI') || (r.includes('OC') && r.includes('FECHA'))) {
            headerRow = i;
            break;
          }
        }

        const rawJson = XLSX.utils.sheet_to_json(ws, { range: headerRow, defval: '' });
        procesarExcel(rawJson);
      } catch (err) {
        console.error('[ControlExhibits] Error al leer Excel:', err);
        setAlertaMensaje({ type: 'error', text: `Error al leer el archivo Excel: ${err.message}` });
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  // ─── 3. Reconciliación Excel / MongoDB vs BD ────────────────────────────────────
  const procesarExcel = (rows, origen = 'excel') => {
    if (!rows || rows.length === 0) {
      setAlertaMensaje({ type: 'error', text: `La fuente (${origen === 'mongo' ? 'MongoDB EA' : 'Excel'}) está vacía o no tiene registros.` });
      return;
    }

    const keys = Object.keys(rows[0] || {});

    const ocKey = keys.find(k => {
      const u = k.trim().toUpperCase();
      return u === 'OC' || u === 'ORDEN' || u === 'O.C.'
        || u.includes('ORDEN DE COMPRA') || u.includes('FOLIO_OC') || u.includes('FOLIO OC');
    });

    const exhibitKey = keys.find(k => {
      const u = k.trim().toUpperCase();
      return u.includes('EXHIBI') || u.includes('EXIBI') || u === 'EXHIBIT';
    });

    const montoKeyExacto = keys.find(k => {
      const u = k.trim().toUpperCase();
      return u === 'IMPORTE' || u === 'MONTO' || u === 'MONTO SOLICITADO' || u === 'MONTO_SOLICITADO';
    });
    const montoKeyParcial = keys.find(k => {
      const u = k.trim().toUpperCase();
      return (u.includes('IMPORTE') || u.includes('MONTO')) && !u.includes('OC') && !u.includes('PROVEEDOR');
    });
    const montoKey = montoKeyExacto || montoKeyParcial || null;

    const fechaKey = keys.find(k => {
      const u = k.trim().toUpperCase();
      return u === 'FECHA' || u === 'FECHA SOLICITUD' || u.includes('FECHA');
    }) || null;

    const provKey = keys.find(k => k.trim().toUpperCase().includes('PROVEEDOR') && !k.trim().toUpperCase().includes('OC')) || null;

    if (!ocKey || !exhibitKey) {
      setAlertaMensaje({
        type: 'error',
        text: `No se encontraron las columnas de OC o Exhibición. Columnas encontradas: ${keys.join(' | ')}`
      });
      return;
    }

    // Construir mapa de BD agrupado por OC → lista de solicitudes (ordenadas por AUTSOLPAGOID ASC)
    const mapaOC = new Map(); // FOLIO_OC_str → [{ autsolpagoid, montoBD, montoBDCentavos, exhibitActual, facturaFecha }]
    solicitudesBD.forEach(r => {
      const oc = String(r.FOLIO_OC || '').trim().replace(/^0+/, '');
      if (!oc) return;
      if (!mapaOC.has(oc)) mapaOC.set(oc, []);
      mapaOC.get(oc).push({
        autsolpagoid: r.AUTSOLPAGOID,
        montoBDCentavos: toCentavos(r.MONTO_SOLICITADO),
        montoBD: r.MONTO_SOLICITADO,
        exhibitActual: String(r.EXHIBICION || '').trim(),
        facturaFecha: r.FACTURA_FECHA ? String(r.FACTURA_FECHA).trim() : null
      });
    });

    const detectadosNuevos = [];
    const detectadosConflictos = [];
    let filasLeidas = 0;
    let filasIgnoradas = 0;

    // Rastrear qué AUTSOLPAGOID ya fue asignado en esta pasada (para no duplicar)
    const usados = new Set();

    // Normalizar y preparar todas las filas entrantes
    const filasPreparadas = [];
    rows.forEach((row) => {
      const ocRaw = String(row[ocKey] || '').trim().replace(/^0+/, '');
      const exhibitExcel = String(row[exhibitKey] || '').trim();

      if (!ocRaw || ocRaw === '0' || ocRaw === '-' || !exhibitExcel) return;
      filasLeidas++;

      const candidatos = mapaOC.get(ocRaw) || [];
      if (candidatos.length === 0) { filasIgnoradas++; return; }

      // Obtener monto del Excel (en centavos)
      const montoExcelCentavos = montoKey ? toCentavos(row[montoKey]) : null;
      const montoExcelValor = montoKey ? row[montoKey] : null;

      // Obtener fecha del Excel en formato YYYY-MM-DD
      let fechaExcelStr = null;
      if (fechaKey && row[fechaKey]) {
        const rawF = String(row[fechaKey]).trim();
        if (rawF.includes('T')) {
          fechaExcelStr = rawF.split('T')[0];
        } else {
          fechaExcelStr = rawF;
        }
      }

      const provExcel = provKey ? String(row[provKey] || '').trim() : '';

      filasPreparadas.push({
        row,
        ocRaw,
        exhibitExcel,
        candidatos,
        montoExcelCentavos,
        montoExcelValor,
        fechaExcelStr,
        provExcel: provExcel && provExcel !== '-' ? provExcel : '',
        asignado: false
      });
    });

    // ─── PASO 1: COINCIDENCIAS EXACTAS POR MONTO (DIFERENCIA <= $1.00 / CENTAVOS) ───
    filasPreparadas.forEach(itemFila => {
      const { candidatos, montoExcelCentavos, exhibitExcel, fechaExcelStr, ocRaw, montoExcelValor, provExcel } = itemFila;

      if (montoExcelCentavos !== null) {
        // Tolerancia de $1.00 (100 centavos) para cubrir redondeos de centavos
        const empatesMonto = candidatos.filter(c => 
          !usados.has(c.autsolpagoid) && 
          Math.abs(c.montoBDCentavos - montoExcelCentavos) <= 100
        );

        let elegido = null;

        if (empatesMonto.length === 1) {
          elegido = empatesMonto[0];
        } else if (empatesMonto.length > 1) {
          // 1. Prioridad: la que ya coincida en Exhibición
          elegido = empatesMonto.find(c => c.exhibitActual === exhibitExcel);

          // 2. Prioridad: filtrar las que tengan FACTURA_FECHA <= Fecha Excel
          if (!elegido && fechaExcelStr) {
            const validasPorFecha = empatesMonto.filter(c => !c.facturaFecha || c.facturaFecha <= fechaExcelStr);
            if (validasPorFecha.length > 0) {
              elegido = validasPorFecha.find(c => !c.exhibitActual) || validasPorFecha[0];
            }
          }

          // 3. Fallback: la primera que no tenga exhibición o la de menor diferencia
          if (!elegido) {
            elegido = empatesMonto.find(c => !c.exhibitActual) || empatesMonto.sort((a, b) => Math.abs(a.montoBDCentavos - montoExcelCentavos) - Math.abs(b.montoBDCentavos - montoExcelCentavos))[0];
          }
        }

        if (elegido) {
          usados.add(elegido.autsolpagoid);
          itemFila.asignado = true;

          const diffPesos = (montoExcelCentavos - elegido.montoBDCentavos) / 100;
          const matchItem = {
            idTemp: elegido.autsolpagoid,
            autsolpagoid: elegido.autsolpagoid,
            oc: ocRaw,
            montoExcelRaw: montoExcelValor !== null && montoExcelValor !== '' ? montoExcelValor : null,
            montoExcelCentavos,
            montoBD: elegido.montoBD,
            montoBDCentavos: elegido.montoBDCentavos,
            diffPesos,
            matchTipo: Math.abs(diffPesos) <= 0.05 ? 'exacto' : 'centavos',
            proveedorExcel: provExcel,
            exhibitExcel,
            exhibitActual: elegido.exhibitActual,
            decisión: 'ACEPTAR'
          };

          if (!elegido.exhibitActual) {
            detectadosNuevos.push({ ...matchItem, incluir: true });
          } else if (elegido.exhibitActual !== exhibitExcel) {
            detectadosConflictos.push(matchItem);
          }
          // Si exhibición ya es igual: queda resuelta y perfecta en silencio
        }
      }
    });

    // ─── PASO 2: COINCIDENCIAS CERCANAS (MARGEN TIPO DE CAMBIO HASTA $500 PESOS) ───
    filasPreparadas.forEach(itemFila => {
      if (itemFila.asignado) return;

      const { candidatos, montoExcelCentavos, exhibitExcel, ocRaw, montoExcelValor, provExcel } = itemFila;

      if (montoExcelCentavos !== null) {
        // Buscar candidatos disponibles de la MISMA OC con diferencia de hasta $500 pesos (50,000 centavos)
        const candidatosCercanos = candidatos
          .filter(c => !usados.has(c.autsolpagoid) && Math.abs(c.montoBDCentavos - montoExcelCentavos) <= 50000)
          .sort((a, b) => Math.abs(a.montoBDCentavos - montoExcelCentavos) - Math.abs(b.montoBDCentavos - montoExcelCentavos));

        if (candidatosCercanos.length > 0) {
          const elegido = candidatosCercanos[0];
          usados.add(elegido.autsolpagoid);
          itemFila.asignado = true;

          const diffPesos = (montoExcelCentavos - elegido.montoBDCentavos) / 100;
          const matchItem = {
            idTemp: elegido.autsolpagoid,
            autsolpagoid: elegido.autsolpagoid,
            oc: ocRaw,
            montoExcelRaw: montoExcelValor !== null && montoExcelValor !== '' ? montoExcelValor : null,
            montoExcelCentavos,
            montoBD: elegido.montoBD,
            montoBDCentavos: elegido.montoBDCentavos,
            diffPesos,
            matchTipo: 'cercano',
            proveedorExcel: provExcel,
            exhibitExcel,
            exhibitActual: elegido.exhibitActual,
            decisión: 'ACEPTAR'
          };

          if (!elegido.exhibitActual) {
            detectadosNuevos.push({ ...matchItem, incluir: true });
          } else if (elegido.exhibitActual !== exhibitExcel) {
            detectadosConflictos.push(matchItem);
          }
        }
      }
    });

    // ─── PASO 3: DETECCIÓN DE SUMA AGRUPADA (MÚLTIPLES EXHIBITS QUE SUMAN UNA SOLICITUD) ───
    // Si una OC tiene 1 solicitud no asignada y 2 o más exhibiciones no asignadas cuya suma de montos
    // coincide exactamente (o diferencia <= $1.00 de centavos) con el monto de la solicitud:
    // Se concatenan los exhibits en formato: "Solicitud A, Solicitud B" y se asignan juntos.
    const remanentesPorOC = new Map();
    filasPreparadas.forEach(itemFila => {
      if (!itemFila.asignado && itemFila.montoExcelCentavos !== null) {
        if (!remanentesPorOC.has(itemFila.ocRaw)) {
          remanentesPorOC.set(itemFila.ocRaw, []);
        }
        remanentesPorOC.get(itemFila.ocRaw).push(itemFila);
      }
    });

    remanentesPorOC.forEach((itemsExh, ocRaw) => {
      if (itemsExh.length < 2) return;

      const candidatos = (mapaOC.get(ocRaw) || []).filter(c => !usados.has(c.autsolpagoid));
      if (!candidatos.length) return;

      candidatos.forEach(elegido => {
        if (usados.has(elegido.autsolpagoid)) return;

        // Calcular la suma total de los exhibits pendientes de esta OC
        const sumaTotalCentavos = itemsExh.reduce((acc, it) => acc + (it.montoExcelCentavos || 0), 0);
        const diffTotalCentavos = Math.abs(sumaTotalCentavos - elegido.montoBDCentavos);

        // Si la suma de TODOS los exhibits pendientes da el monto de la solicitud (tolerancia 100 centavos = $1.00):
        if (diffTotalCentavos <= 100) {
          usados.add(elegido.autsolpagoid);
          itemsExh.forEach(it => { it.asignado = true; });

          const exhibitsConcatenados = itemsExh.map(it => it.exhibitExcel).join(', ');
          const diffPesos = (sumaTotalCentavos - elegido.montoBDCentavos) / 100;

          const matchItem = {
            idTemp: elegido.autsolpagoid,
            autsolpagoid: elegido.autsolpagoid,
            oc: ocRaw,
            montoExcelRaw: `$${(sumaTotalCentavos / 100).toLocaleString('es-MX', { minimumFractionDigits: 2 })} (Suma de ${itemsExh.length} exhibits)`,
            montoExcelCentavos: sumaTotalCentavos,
            montoBD: elegido.montoBD,
            montoBDCentavos: elegido.montoBDCentavos,
            diffPesos,
            matchTipo: 'suma_agrupada',
            proveedorExcel: itemsExh[0]?.provExcel || '',
            exhibitExcel: exhibitsConcatenados,
            exhibitActual: elegido.exhibitActual,
            decisión: 'ACEPTAR'
          };

          if (!elegido.exhibitActual) {
            detectadosNuevos.push({ ...matchItem, incluir: true });
          } else if (elegido.exhibitActual !== exhibitsConcatenados) {
            detectadosConflictos.push(matchItem);
          }
        }
      });
    });

    // ─── FILAS NO ASIGNADAS (SIN COINCIDENCIA POR MONTO) ───
    // Si no coincidió en Paso 1 (exacto/centavos) ni en Paso 2 (tolerancia cercana tipo de cambio <= $500),
    // NUNCA asignar a ciegas a otra solicitud con monto diferente. Se ignora de forma segura.
    filasPreparadas.forEach(itemFila => {
      if (!itemFila.asignado) {
        filasIgnoradas++;
      }
    });

    setResumen({
      leidas: filasLeidas,
      ignoradas: filasIgnoradas,
      sinMontoDetectado: !montoKey,
      columnasMonto: montoKey || '(ninguna detectada)',
      origen
    });

    if (detectadosConflictos.length === 0 && detectadosNuevos.length === 0) {
      setAlertaMensaje({
        type: 'success',
        text: `✅ Todo está al día con ${origen === 'mongo' ? 'MongoDB (EA)' : 'el Excel'}. ${filasLeidas} registros procesados — todas las solicitudes encontradas en BD ya tienen la exhibición correcta. (${filasIgnoradas} registros ignorados por no encontrar solicitud en BD)`
      });
      return;
    }

    setConflictos(detectadosConflictos);
    setRegistrosNuevos(detectadosNuevos);
    setModalOpen(true);
  };

  // ─── 4. Guardar cambios ────────────────────────────────────────────────────────
  const guardarCambios = async () => {
    const paraActualizar = [
      ...registrosNuevos.filter(n => n.incluir !== false),
      ...conflictos.filter(c => c.decisión === 'ACEPTAR')
    ].map(i => ({ autsolpagoid: i.autsolpagoid, exhibicionNueva: i.exhibitExcel, exhibicionAnterior: i.exhibitActual }));

    if (paraActualizar.length === 0) {
      setModalOpen(false);
      setAlertaMensaje({ type: 'warning', text: 'No se seleccionaron cambios para aplicar.' });
      return;
    }

    setGuardando(true);
    setProgreso(0);
    setProgresoMsg(`Preparando ${paraActualizar.length} actualizaciones...`);

    try {
      const chunkSize = 500;
      const totalChunks = Math.ceil(paraActualizar.length / chunkSize);
      let totalActualizados = 0;

      for (let i = 0; i < totalChunks; i++) {
        const chunk = paraActualizar.slice(i * chunkSize, (i + 1) * chunkSize);
        setProgresoMsg(`Guardando lote ${i + 1} de ${totalChunks} (${chunk.length} registros)...`);
        const result = await axios.post(`${apiUrl}/actualizarExhibitsBatch`, {
          idEmpresa: 1, usuario: loggedInUsername || 'Sistema', items: chunk
        });
        totalActualizados += result.data?.actualizados || chunk.length;
        setProgreso(Math.round(((i + 1) / totalChunks) * 100));
      }

      setProgreso(100);
      setProgresoMsg('¡Completado!');
      await new Promise(r => setTimeout(r, 600));

      setModalOpen(false);
      setAlertaMensaje({ type: 'success', text: `✅ ¡Éxito! Se actualizaron ${totalActualizados} solicitudes de pago.` });
      fetchSolicitudesBD();
    } catch (err) {
      console.error('Error al guardar:', err);
      setAlertaMensaje({ type: 'error', text: `❌ Error al guardar: ${err.response?.data?.mensaje || err.message}` });
    } finally {
      setGuardando(false);
      setProgreso(0);
      setProgresoMsg('');
    }
  };

  const handleDesvincularExhibit = async (row) => {
    const res = await handleOpenSweetAlert(
      `¿Deseas desvincular la exhibición "${row.EXHIBICION}" de la solicitud #${row.AUTSOLPAGOID} (OC: ${row.FOLIO_OC}, Monto: $${Number(row.MONTO_SOLICITADO || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})?`,
      'warning',
      {
        showCancelButton: true,
        confirmButtonText: 'Sí, desvincular',
        cancelButtonText: 'Cancelar'
      }
    );
    if (!res?.isConfirmed) return;

    setLoading(true);
    try {
      await axios.post(`${apiUrl}/actualizarExhibitsBatch`, {
        idEmpresa: 1,
        usuario: loggedInUsername || 'Sistema',
        items: [{
          autsolpagoid: row.AUTSOLPAGOID,
          exhibicionNueva: '',
          exhibicionAnterior: row.EXHIBICION
        }]
      });
      handleOpenSweetAlert(
        `Se desvinculó la exhibición "${row.EXHIBICION}" de la solicitud #${row.AUTSOLPAGOID} correctamente.`,
        'success'
      );
      fetchSolicitudesBD();
    } catch (err) {
      console.error('Error al desvincular exhibición:', err);
      handleOpenSweetAlert(`Error al quitar exhibición: ${err.response?.data?.mensaje || err.message}`, 'error');
      setLoading(false);
    }
  };

  const toggleDecision = (idTemp) =>
    setConflictos(prev => prev.map(item =>
      item.idTemp === idTemp ? { ...item, decisión: item.decisión === 'ACEPTAR' ? 'CONSERVAR' : 'ACEPTAR' } : item
    ));

  const aplicarDecisionTodos = (tipo) =>
    setConflictos(prev => prev.map(item => ({ ...item, decisión: tipo })));

  const toggleIncluirNuevo = (idTemp) =>
    setRegistrosNuevos(prev => prev.map(item =>
      item.idTemp === idTemp ? { ...item, incluir: !item.incluir } : item
    ));

  const aplicarIncluirTodosNuevos = (incluir) =>
    setRegistrosNuevos(prev => prev.map(item => ({ ...item, incluir })));

  const tablaVista = useMemo(() => {
    const conExh = solicitudesBD.filter(r => r.EXHIBICION && String(r.EXHIBICION).trim() !== '');
    if (!searchTerm) return conExh.slice(0, 500);
    const t = searchTerm.toLowerCase();
    return conExh.filter(r =>
      String(r.FOLIO_OC || '').toLowerCase().includes(t) ||
      String(r.EXHIBICION || '').toLowerCase().includes(t) ||
      String(r.MONTO_SOLICITADO || '').toLowerCase().includes(t)
    ).slice(0, 500);
  }, [solicitudesBD, searchTerm]);

  const totalAGuardar = registrosNuevos.filter(n => n.incluir !== false).length + conflictos.filter(c => c.decisión === 'ACEPTAR').length;

  // Helper para mostrar monto
  const displayMonto = (centavos, raw) => {
    if (centavos !== null) return `$${formatMonto(centavos)}`;
    if (raw !== null && raw !== undefined && raw !== '') return `${raw}`;
    return <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 10 }}>—</span>;
  };

  // Columnas de la tabla del modal (nueva vs existente)
  const colStyle = {
    fontSize: 'clamp(0.68rem, 0.65rem + 0.1vw, 0.76rem)',
    fontWeight: 800,
    py: 'clamp(6px, 0.7vw, 9px)',
    px: 'clamp(6px, 0.9vw, 10px)',
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap'
  };

  return (
    <Box sx={{ p: 'clamp(8px, 1.2vw, 16px)', bgcolor: '#f8fafc', minHeight: '100%', overflowY: 'auto' }}>

      {/* ── ENCABEZADO Y TARJETAS KPI PREMIUM COMPACTO ── */}
      <Paper
        elevation={0}
        sx={{
          p: 'clamp(8px, 1.2vw, 14px)',
          mb: 'clamp(8px, 1.2vw, 14px)',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.02)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          {/* Título y Subtítulo */}
          <Box sx={{ flex: 1, minWidth: 'min(100%, 260px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  color: '#0f172a',
                  fontSize: 'clamp(0.85rem, 0.82rem + 0.25vw, 1.10rem)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.2
                }}
              >
                Control y Conciliación de Exhibits por OC
              </Typography>
              <Chip
                label="ESEASA OFFSHORE"
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: 'rgba(6, 85, 165, 0.08)',
                  color: '#0655A5',
                  border: '1px solid rgba(6, 85, 165, 0.2)',
                  fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.65rem)',
                  height: 'clamp(18px, 1.6vw, 22px)'
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                mt: 0.2,
                fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)'
              }}
            >
              Matching inteligente por <strong>FOLIO_OC + Importe</strong>. Concilia y actualiza números de exhibición en masa.
            </Typography>

            {/* Tarjetas KPI Informativas Compactas */}
            {!loading && (
              <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    px: 'clamp(6px, 0.8vw, 10px)',
                    py: 'clamp(2px, 0.3vw, 4px)',
                    borderRadius: '6px',
                    bgcolor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0.6
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', textTransform: 'uppercase' }}>
                    Total:
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 900, fontSize: 'clamp(0.68rem, 0.65rem + 0.06vw, 0.78rem)' }}>
                    {stats.total.toLocaleString('es-MX')}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 'clamp(6px, 0.8vw, 10px)',
                    py: 'clamp(2px, 0.3vw, 4px)',
                    borderRadius: '6px',
                    bgcolor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0.6
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', textTransform: 'uppercase' }}>
                    Con Exhibición:
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: '#15803d', fontWeight: 900, fontSize: 'clamp(0.68rem, 0.65rem + 0.06vw, 0.78rem)' }}>
                    {stats.conExhibicion.toLocaleString('es-MX')}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 'clamp(6px, 0.8vw, 10px)',
                    py: 'clamp(2px, 0.3vw, 4px)',
                    borderRadius: '6px',
                    bgcolor: '#fffbeb',
                    border: '1px solid #fde68a',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0.6
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 700, fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', textTransform: 'uppercase' }}>
                    Sin Exhibición:
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: '#b45309', fontWeight: 900, fontSize: 'clamp(0.68rem, 0.65rem + 0.06vw, 0.78rem)' }}>
                    {(stats.total - stats.conExhibicion).toLocaleString('es-MX')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Botones de Acción */}
          <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={auditando ? <CircularProgress size={13} color="inherit" /> : <FactCheck sx={{ fontSize: 'clamp(13px, 1vw, 16px)' }} />}
              disabled={loading || syncingMongo || auditando}
              onClick={handleAuditarDiscrepancias}
              sx={{
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: '6px',
                px: 'clamp(8px, 1vw, 12px)',
                py: 'clamp(3px, 0.4vw, 5px)',
                fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                boxShadow: '0 1px 4px rgba(245, 158, 11, 0.25)',
                whiteSpace: 'nowrap',
                height: 'clamp(26px, 2.2vw, 32px)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.35)'
                }
              }}
            >
              {auditando ? 'Comparando...' : '⚖️ Comparar Discrepancias'}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={syncingMongo ? <CircularProgress size={13} color="inherit" /> : <CloudSync sx={{ fontSize: 'clamp(13px, 1vw, 16px)' }} />}
              disabled={loading || syncingMongo}
              onClick={handleSyncMongo}
              sx={{
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: '6px',
                px: 'clamp(8px, 1vw, 12px)',
                py: 'clamp(3px, 0.4vw, 5px)',
                fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 1px 4px rgba(16, 185, 129, 0.25)',
                whiteSpace: 'nowrap',
                height: 'clamp(26px, 2.2vw, 32px)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.35)'
                }
              }}
            >
              {syncingMongo ? 'Consultando EA...' : '⚡ Sincronizar desde MongoDB (EA)'}
            </Button>
            <Button
              variant="outlined"
              component="label"
              size="small"
              startIcon={loading ? <CircularProgress size={13} color="inherit" /> : <CloudUpload sx={{ fontSize: 'clamp(13px, 1vw, 16px)' }} />}
              disabled={loading || syncingMongo}
              sx={{
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: '6px',
                px: 'clamp(8px, 1vw, 12px)',
                py: 'clamp(3px, 0.4vw, 5px)',
                fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
                borderColor: '#cbd5e1',
                color: '#334155',
                bgcolor: '#ffffff',
                whiteSpace: 'nowrap',
                height: 'clamp(26px, 2.2vw, 32px)',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                  borderColor: '#94a3b8'
                }
              }}
            >
              Importar Excel
              <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileUpload} />
            </Button>
            <Tooltip title="Recargar solicitudes de base de datos">
              <IconButton
                size="small"
                onClick={fetchSolicitudesBD}
                disabled={loading}
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  bgcolor: '#ffffff',
                  p: 'clamp(3px, 0.4vw, 5px)',
                  height: 'clamp(26px, 2.2vw, 32px)',
                  width: 'clamp(26px, 2.2vw, 32px)',
                  '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                }}
              >
                <Refresh sx={{ fontSize: 'clamp(13px, 1vw, 17px)', color: '#475569' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {loading && (
          <Box sx={{ mt: 0.8 }}>
            <LinearProgress sx={{ height: 2.5, borderRadius: '2px' }} />
            <Typography sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.68rem)', color: '#64748b', mt: 0.3, fontWeight: 600 }}>
              Consultando solicitudes y exhibiciones desde la base de datos...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* ── ALERTAS ── */}
      {alertaMensaje && (
        <Alert
          severity={alertaMensaje.type}
          onClose={() => setAlertaMensaje(null)}
          sx={{
            mb: 'clamp(12px, 1.5vw, 18px)',
            borderRadius: '10px',
            fontWeight: 700,
            fontSize: 'clamp(0.74rem, 0.7rem + 0.08vw, 0.82rem)',
            py: 0.5
          }}
        >
          {alertaMensaje.text}
        </Alert>
      )}

      {/* ── TABLA DE VISTA ── */}
      <Paper
        elevation={0}
        sx={{
          p: 'clamp(10px, 1.2vw, 16px)',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.02)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontSize: 'clamp(0.76rem, 0.72rem + 0.1vw, 0.88rem)',
                fontWeight: 900,
                color: '#0f172a'
              }}
            >
              Solicitudes con Exhibición Registrada
            </Typography>
            <Chip
              label={`${stats.conExhibicion.toLocaleString('es-MX')} total`}
              size="small"
              sx={{
                fontWeight: 800,
                bgcolor: 'rgba(6, 85, 165, 0.08)',
                color: '#0655A5',
                fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.65rem)',
                height: 'clamp(18px, 1.6vw, 22px)'
              }}
            />
            <Chip
              label="Vista: primeras 500"
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: '#f1f5f9',
                color: '#64748b',
                fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.64rem)',
                height: 'clamp(18px, 1.6vw, 22px)'
              }}
            />
          </Box>
          <TextField
            size="small"
            placeholder="Buscar OC, Exhibición o Monto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: 'clamp(180px, 18vw, 260px)',
              '& .MuiOutlinedInput-root': {
                height: 'clamp(26px, 2.2vw, 32px)',
                borderRadius: '7px',
                fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
                bgcolor: '#f8fafc',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0655A5' }
              }
            }}
          />
        </Box>

        <Box sx={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
          <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'clamp(4px, 0.5vw, 6px)', px: 'clamp(6px, 0.8vw, 10px)', borderBottom: '1px solid #f1f5f9' } }}>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={colStyle}>FOLIO OC</TableCell>
                <TableCell sx={colStyle}>MONTO SOLICITADO (BD)</TableCell>
                <TableCell sx={colStyle}>EXHIBICIÓN ASIGNADA</TableCell>
                <TableCell sx={colStyle}>ID SOLICITUD</TableCell>
                <TableCell sx={{ ...colStyle, textAlign: 'center' }}>ACCIONES</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} sx={{ color: '#0655A5' }} />
                  </TableCell>
                </TableRow>
              ) : tablaVista.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#94a3b8', fontStyle: 'italic', fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)' }}>
                    No hay registros de exhibiciones para mostrar.
                  </TableCell>
                </TableRow>
              ) : tablaVista.map((row, idx) => (
                <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell sx={{ fontWeight: 900, fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.76rem)', color: '#0655A5' }}>
                    {row.FOLIO_OC}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)', color: '#334155' }}>
                    ${Number(row.MONTO_SOLICITADO || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.EXHIBICION}
                      size="small"
                      sx={{
                        fontWeight: 900,
                        fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)',
                        bgcolor: 'rgba(6, 85, 165, 0.08)',
                        color: '#0655A5',
                        borderRadius: '5px',
                        border: '1px solid rgba(6, 85, 165, 0.2)',
                        height: 'clamp(18px, 1.6vw, 22px)'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', color: '#94a3b8', fontWeight: 700 }}>
                    #{row.AUTSOLPAGOID}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Desvincular o quitar exhibición de esta solicitud">
                      <IconButton
                        size="small"
                        disabled={loading || guardando}
                        onClick={() => handleDesvincularExhibit(row)}
                        sx={{
                          p: '3px',
                          borderRadius: '6px',
                          border: '1px solid #fee2e2',
                          bgcolor: '#ffffff',
                          color: '#dc2626',
                          '&:hover': { bgcolor: '#fef2f2', borderColor: '#fca5a5' }
                        }}
                      >
                        <DeleteOutline sx={{ fontSize: 'clamp(13px, 1vw, 16px)' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* ── MODAL DE CONCILIACIÓN PREMIUM ── */}
      <Dialog
        open={modalOpen}
        disableRestoreFocus
        onClose={() => !guardando && setModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0',
            maxHeight: '92vh'
          }
        }}
      >
        {/* Header Premium con Gradiente Oficial del Sistema */}
        <Box
          sx={{
            px: 'clamp(12px, 1.5vw, 20px)',
            py: 'clamp(9px, 1.1vw, 13px)',
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 900,
                fontSize: 'clamp(0.85rem, 0.8rem + 0.22vw, 1.05rem)',
                lineHeight: 1.2,
                letterSpacing: '-0.01em'
              }}
              noWrap
            >
              Resultado de Conciliación {resumen.origen === 'mongo' ? 'MongoDB (EA)' : 'Excel'} vs Base de Datos
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.88,
                fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.74rem)',
                mt: 0.2,
                display: 'block'
              }}
            >
              Revisa las coincidencias, discrepancias y nuevas asignaciones antes de confirmar
            </Typography>
          </Box>

          <IconButton
            onClick={() => !guardando && setModalOpen(false)}
            size="small"
            disabled={guardando}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.12)',
              p: '4px',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            <CloseIcon fontSize="small" sx={{ fontSize: 'clamp(15px, 1.1vw, 18px)' }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 'clamp(10px, 1.4vw, 18px)', bgcolor: '#f8fafc' }}>

          {/* Progreso de guardado */}
          {guardando && (
            <Box
              sx={{
                mb: 1.5,
                p: 'clamp(8px, 1vw, 12px)',
                bgcolor: '#f0fdf4',
                borderRadius: '10px',
                border: '1px solid #bbf7d0'
              }}
            >
              <Typography sx={{ fontSize: 'clamp(0.74rem, 0.7rem + 0.08vw, 0.82rem)', fontWeight: 900, color: '#166534', mb: 0.5 }}>
                Guardando en base de datos...
              </Typography>
              <LinearProgress variant="determinate" value={progreso} sx={{ height: 6, borderRadius: '3px', mb: 0.5 }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#16a34a', fontWeight: 800 }}>
                {progresoMsg} ({progreso}%)
              </Typography>
            </Box>
          )}

          {/* Advertencia si no se detectó columna de monto */}
          {resumen.sinMontoDetectado && (
            <Alert severity="warning" sx={{ mb: 1.5, fontWeight: 700, borderRadius: '8px', py: 0.2, fontSize: '0.74rem' }}>
              No se detectó columna de monto en la fuente de datos. El matching se hizo solo por FOLIO_OC.
            </Alert>
          )}

          {/* Chips de resumen */}
          <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              size="small"
              label={`🍃 Origen: ${resumen.origen === 'mongo' ? 'MongoDB EA' : 'Archivo Excel'}`}
              sx={{ fontWeight: 800, bgcolor: resumen.origen === 'mongo' ? '#ecfdf5' : '#eff6ff', color: resumen.origen === 'mongo' ? '#047857' : '#1d4ed8', border: `1px solid ${resumen.origen === 'mongo' ? '#a7f3d0' : '#bfdbfe'}`, fontSize: 'clamp(0.65rem, 0.62rem + 0.05vw, 0.72rem)', height: 22 }}
            />
            <Chip
              size="small"
              label={`📄 ${resumen.leidas} registros leídos`}
              sx={{ fontWeight: 800, bgcolor: '#ffffff', color: '#334155', border: '1px solid #e2e8f0', fontSize: 'clamp(0.65rem, 0.62rem + 0.05vw, 0.72rem)', height: 22 }}
            />
            {resumen.ignoradas > 0 && (
              <Chip
                size="small"
                label={`⏭ ${resumen.ignoradas} ignoradas (no en BD)`}
                sx={{ fontWeight: 800, bgcolor: '#fef9c3', color: '#854d0e', border: '1px solid #fde047', fontSize: 'clamp(0.65rem, 0.62rem + 0.05vw, 0.72rem)', height: 22 }}
              />
            )}
            <Chip
              size="small"
              label={`✨ ${registrosNuevos.length} nuevas`}
              sx={{ fontWeight: 800, bgcolor: 'rgba(6, 85, 165, 0.08)', color: '#0655A5', border: '1px solid rgba(6, 85, 165, 0.2)', fontSize: 'clamp(0.65rem, 0.62rem + 0.05vw, 0.72rem)', height: 22 }}
            />
            {conflictos.length > 0 && (
              <Chip
                size="small"
                label={`⚠️ ${conflictos.length} discrepancias`}
                sx={{ fontWeight: 800, bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontSize: 'clamp(0.65rem, 0.62rem + 0.05vw, 0.72rem)', height: 22 }}
              />
            )}
            <Chip
              size="small"
              label={`Columna monto: "${resumen.columnasMonto}"`}
              sx={{ fontWeight: 700, bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', fontSize: 'clamp(0.62rem, 0.6rem + 0.05vw, 0.68rem)', height: 22 }}
            />
          </Box>

          {/* ── DISCREPANCIAS ── */}
          {conflictos.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 1,
                  bgcolor: '#ffffff',
                  p: 'clamp(6px, 0.8vw, 10px)',
                  borderRadius: '10px',
                  border: '1px solid #fde68a',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography sx={{ fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.78rem)', fontWeight: 800, color: '#92400e' }}>
                  ⚠️ {conflictos.length} discrepancias detectadas:
                </Typography>
                <Stack direction="row" spacing={0.8}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={() => aplicarDecisionTodos('ACEPTAR')}
                    sx={{ fontWeight: 800, textTransform: 'none', borderRadius: '6px', fontSize: 'clamp(0.64rem, 0.6rem + 0.05vw, 0.7rem)', py: 0.1, px: 1 }}
                  >
                    🟢 Usar todas del Excel
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() => aplicarDecisionTodos('CONSERVAR')}
                    sx={{ fontWeight: 800, textTransform: 'none', borderRadius: '6px', fontSize: 'clamp(0.64rem, 0.6rem + 0.05vw, 0.7rem)', py: 0.1, px: 1 }}
                  >
                    🔵 Conservar todas de BD
                  </Button>
                </Stack>
              </Box>

              <Box
                sx={{
                  maxHeight: 'clamp(160px, 24vh, 240px)',
                  overflowY: 'auto',
                  border: '1px solid #fde68a',
                  borderRadius: '10px',
                  bgcolor: '#ffffff'
                }}
              >
                <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: 'clamp(4px, 0.5vw, 7px)', px: 'clamp(6px, 0.8vw, 10px)' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...colStyle, bgcolor: '#fffbeb', color: '#78350f' }}>FOLIO OC</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#fffbeb', color: '#78350f' }}>MONTO EXCEL</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#fffbeb', color: '#78350f' }}>MONTO BD</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#fffbeb', color: '#78350f' }}>MATCH</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#fffbeb', color: '#78350f' }}>EXHIBICIÓN EN BD</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#fffbeb', color: '#78350f' }}>EXHIBICIÓN EXCEL</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#fffbeb', color: '#78350f' }}>#SOL</TableCell>
                      <TableCell align="center" sx={{ ...colStyle, bgcolor: '#fffbeb', color: '#78350f' }}>DECISIÓN</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conflictos.map((c) => (
                      <TableRow key={c.idTemp} hover sx={{ bgcolor: c.matchTipo === 'fallback' ? '#fffbeb' : 'inherit' }}>
                        <TableCell sx={{ fontWeight: 900, fontSize: 'clamp(0.7rem, 0.68rem + 0.06vw, 0.78rem)', color: '#0655A5' }}>{c.oc}</TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.05vw, 0.74rem)', color: '#334155', fontWeight: 700 }}>
                          {displayMonto(c.montoExcelCentavos, c.montoExcelRaw)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.05vw, 0.74rem)', color: '#334155', fontWeight: 700 }}>
                          {displayMonto(c.montoBDCentavos, c.montoBD)}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const diff = Math.abs(c.diffPesos || 0);
                            const diffStr = diff > 0 ? ` (±$${diff.toFixed(2)})` : '';
                            let label = '✓ Exacto';
                            let bg = '#dcfce7';
                            let color = '#166534';

                            if (c.matchTipo === 'centavos') {
                              label = `✓ Centavos${diffStr}`;
                              bg = '#ecfdf5';
                              color = '#047857';
                            } else if (c.matchTipo === 'suma_agrupada') {
                              label = `Σ Suma combinada${diffStr}`;
                              bg = '#f0fdf4';
                              color = '#15803d';
                            } else if (c.matchTipo === 'cercano') {
                              label = `~ Aprox${diffStr}`;
                              bg = '#fef9c3';
                              color = '#854d0e';
                            } else if (c.matchTipo === 'fallback') {
                              label = `~ Parcial${diffStr}`;
                              bg = '#ffedd5';
                              color = '#9a3412';
                            }

                            return (
                              <Chip
                                label={label}
                                size="small"
                                sx={{
                                  fontSize: '0.62rem',
                                  fontWeight: 800,
                                  bgcolor: bg,
                                  color: color,
                                  height: 20
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.05vw, 0.74rem)', color: '#dc2626', fontWeight: 800 }}>{c.exhibitActual}</TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.05vw, 0.74rem)', color: '#16a34a', fontWeight: 900 }}>{c.exhibitExcel}</TableCell>
                        <TableCell sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600 }}>#{c.autsolpagoid}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant={c.decisión === 'ACEPTAR' ? 'contained' : 'outlined'}
                            color={c.decisión === 'ACEPTAR' ? 'success' : 'warning'}
                            onClick={() => toggleDecision(c.idTemp)}
                            sx={{ textTransform: 'none', fontWeight: 900, fontSize: '0.64rem', py: 0.1, px: 0.8, minWidth: 50, borderRadius: '5px' }}
                          >
                            {c.decisión === 'ACEPTAR' ? '✓ Excel' : '⊘ BD'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {/* ── NUEVAS ASIGNACIONES ── */}
          {registrosNuevos.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 1,
                  bgcolor: '#ffffff',
                  p: 'clamp(6px, 0.8vw, 10px)',
                  borderRadius: '10px',
                  border: '1px solid rgba(6, 85, 165, 0.2)',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Typography sx={{ fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.78rem)', fontWeight: 800, color: '#0655A5' }}>
                    ✨ {registrosNuevos.length.toLocaleString('es-MX')} nuevas asignaciones:
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600 }}>
                    ({registrosNuevos.filter(n => n.incluir !== false).length} activas)
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.8}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={() => aplicarIncluirTodosNuevos(true)}
                    sx={{ fontWeight: 800, textTransform: 'none', borderRadius: '6px', fontSize: 'clamp(0.64rem, 0.6rem + 0.05vw, 0.7rem)', py: 0.1, px: 1 }}
                  >
                    🟢 Incluir todas
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => aplicarIncluirTodosNuevos(false)}
                    sx={{ fontWeight: 800, textTransform: 'none', borderRadius: '6px', fontSize: 'clamp(0.64rem, 0.6rem + 0.05vw, 0.7rem)', py: 0.1, px: 1 }}
                  >
                    🔴 Excluir todas
                  </Button>
                </Stack>
              </Box>

              <Box
                sx={{
                  maxHeight: 'clamp(200px, 34vh, 320px)',
                  overflowY: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  bgcolor: '#ffffff'
                }}
              >
                <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: 'clamp(4px, 0.5vw, 7px)', px: 'clamp(6px, 0.8vw, 10px)' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...colStyle, bgcolor: '#f8fafc', color: '#0655A5' }}>FOLIO OC</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#f8fafc', color: '#0655A5' }}>MONTO EXCEL</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#f8fafc', color: '#0655A5' }}>MONTO BD</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#f8fafc', color: '#0655A5' }}>MATCH</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#f8fafc', color: '#0655A5' }}>PROVEEDOR</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#f8fafc', color: '#0655A5' }}>EXHIBICIÓN A ASIGNAR</TableCell>
                      <TableCell sx={{ ...colStyle, bgcolor: '#f8fafc', color: '#0655A5' }}>#SOL</TableCell>
                      <TableCell align="center" sx={{ ...colStyle, bgcolor: '#f8fafc', color: '#0655A5' }}>INCLUIR</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {registrosNuevos.map((n, idx) => (
                      <TableRow key={idx} hover sx={{ bgcolor: n.matchTipo === 'fallback' ? '#fffbeb' : 'inherit' }}>
                        <TableCell sx={{ fontWeight: 900, fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.78rem)', color: '#0655A5' }}>{n.oc}</TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.05vw, 0.74rem)', color: '#334155', fontWeight: 700 }}>
                          {displayMonto(n.montoExcelCentavos, n.montoExcelRaw)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.05vw, 0.74rem)', color: '#334155', fontWeight: 700 }}>
                          {displayMonto(n.montoBDCentavos, n.montoBD)}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const diff = Math.abs(n.diffPesos || 0);
                            const diffStr = diff > 0 ? ` (±$${diff.toFixed(2)})` : '';
                            let label = '✓ Exacto';
                            let bg = '#dcfce7';
                            let color = '#166534';

                            if (n.matchTipo === 'centavos') {
                              label = `✓ Centavos${diffStr}`;
                              bg = '#ecfdf5';
                              color = '#047857';
                            } else if (n.matchTipo === 'suma_agrupada') {
                              label = `Σ Suma combinada${diffStr}`;
                              bg = '#f0fdf4';
                              color = '#15803d';
                            } else if (n.matchTipo === 'cercano') {
                              label = `~ Aprox${diffStr}`;
                              bg = '#fef9c3';
                              color = '#854d0e';
                            } else if (n.matchTipo === 'fallback') {
                              label = `~ Parcial${diffStr}`;
                              bg = '#ffedd5';
                              color = '#9a3412';
                            }

                            return (
                              <Chip
                                label={label}
                                size="small"
                                sx={{
                                  fontSize: '0.62rem',
                                  fontWeight: 800,
                                  bgcolor: bg,
                                  color: color,
                                  height: 20
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.66rem, 0.62rem + 0.05vw, 0.72rem)', color: '#334155', fontWeight: 600 }}>{n.proveedorExcel || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.72rem, 0.68rem + 0.06vw, 0.78rem)', color: '#0655A5', fontWeight: 900 }}>{n.exhibitExcel}</TableCell>
                        <TableCell sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600 }}>#{n.autsolpagoid}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant={n.incluir !== false ? 'contained' : 'outlined'}
                            color={n.incluir !== false ? 'success' : 'error'}
                            onClick={() => toggleIncluirNuevo(n.idTemp)}
                            sx={{ textTransform: 'none', fontWeight: 900, fontSize: '0.64rem', py: 0.1, px: 0.8, minWidth: 54, borderRadius: '5px' }}
                          >
                            {n.incluir !== false ? '✓ Incluir' : '✗ Excluir'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </DialogContent>

        {/* Footer Premium */}
        <DialogActions
          sx={{
            px: 'clamp(12px, 1.5vw, 20px)',
            py: 'clamp(8px, 1vw, 12px)',
            bgcolor: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#475569',
              fontWeight: 700,
              fontSize: 'clamp(0.68rem, 0.65rem + 0.06vw, 0.76rem)'
            }}
          >
            <strong>{totalAGuardar.toLocaleString('es-MX')}</strong> asignaciones listas para guardar
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              onClick={() => setModalOpen(false)}
              disabled={guardando}
              sx={{
                borderRadius: '6px',
                px: 'clamp(10px, 1.2vw, 14px)',
                fontWeight: 700,
                fontSize: 'clamp(0.7rem, 0.66rem + 0.06vw, 0.78rem)',
                textTransform: 'none',
                color: '#64748b'
              }}
            >
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={guardarCambios}
              disabled={guardando || totalAGuardar === 0}
              startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : <CheckCircle sx={{ fontSize: 'clamp(15px, 1.1vw, 18px)' }} />}
              sx={{
                borderRadius: '8px',
                px: 'clamp(14px, 1.6vw, 20px)',
                py: 'clamp(5px, 0.6vw, 7px)',
                fontWeight: 800,
                fontSize: 'clamp(0.72rem, 0.68rem + 0.08vw, 0.82rem)',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                boxShadow: '0 3px 10px rgba(6, 85, 165, 0.28)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #054785 0%, #0655A5 100%)',
                  boxShadow: '0 5px 16px rgba(6, 85, 165, 0.4)'
                }
              }}
            >
              {guardando ? `Guardando... ${progreso}%` : `Confirmar y Guardar (${totalAGuardar.toLocaleString('es-MX')} solicitudes)`}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* ── MODAL DE AUDITORÍA DE DISCREPANCIAS CON EA ── */}
      <Dialog
        open={modalAuditoriaOpen}
        disableRestoreFocus
        onClose={() => !guardandoAuditoria && setModalAuditoriaOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0',
            maxHeight: '92vh'
          }
        }}
      >
        <Box
          sx={{
            px: 'clamp(12px, 1.5vw, 20px)',
            py: 'clamp(9px, 1.1vw, 13px)',
            background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 900, fontSize: 'clamp(0.85rem, 0.8rem + 0.22vw, 1.05rem)', lineHeight: 1.2 }}
            >
              ⚖️ Auditoría de Discrepancias: Exhibiciones en BD vs MongoDB (EA)
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.9, fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.74rem)', mt: 0.2, display: 'block' }}
            >
              Se detectaron solicitudes cuyo exhibit actual en BD difiere en monto o en OC del catálogo oficial de EA.
            </Typography>
          </Box>
          <IconButton
            onClick={() => !guardandoAuditoria && setModalAuditoriaOpen(false)}
            size="small"
            disabled={guardandoAuditoria}
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', p: '4px', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 'clamp(10px, 1.2vw, 16px)', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {/* Header con botón para seleccionar todas y resumen */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, p: 1, bgcolor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={`${discrepancias.length} discrepancia${discrepancias.length === 1 ? '' : 's'} detectada${discrepancias.length === 1 ? '' : 's'}`}
                color="warning"
                size="small"
                sx={{ fontWeight: 800, fontSize: '0.72rem' }}
              />
              <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                Seleccionadas: <strong>{discrepancias.filter(d => d.seleccionado).length}</strong> de {discrepancias.length}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.8}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleToggleSeleccionarTodasDiscrepancias(true)}
                disabled={guardandoAuditoria || (discrepancias.length > 0 && discrepancias.every(d => d.seleccionado))}
                sx={{ textTransform: 'none', fontSize: '0.68rem', fontWeight: 700 }}
              >
                Seleccionar todas
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleToggleSeleccionarTodasDiscrepancias(false)}
                disabled={guardandoAuditoria || !discrepancias.some(d => d.seleccionado)}
                sx={{ textTransform: 'none', fontSize: '0.68rem', fontWeight: 700 }}
              >
                Deseleccionar todas
              </Button>
            </Stack>
          </Box>

          {/* Tabla de Discrepancias */}
          <Box sx={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell padding="checkbox" align="center">
                    <Checkbox
                      size="small"
                      checked={discrepancias.length > 0 && discrepancias.every(d => d.seleccionado)}
                      indeterminate={discrepancias.some(d => d.seleccionado) && !discrepancias.every(d => d.seleccionado)}
                      onChange={(e) => handleToggleSeleccionarTodasDiscrepancias(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={colStyle}>FOLIO OC</TableCell>
                  <TableCell sx={colStyle}>ID SOLICITUD</TableCell>
                  <TableCell sx={colStyle}>EXHIBIT ACTUAL (BD)</TableCell>
                  <TableCell sx={colStyle}>MONTO BD</TableCell>
                  <TableCell sx={colStyle}>MONTO OFICIAL EA</TableCell>
                  <TableCell sx={colStyle}>DIFERENCIA</TableCell>
                  <TableCell sx={colStyle}>DIAGNÓSTICO</TableCell>
                  <TableCell sx={{ ...colStyle, textAlign: 'center' }}>ACCIONES</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {discrepancias.map((d) => (
                  <TableRow key={d.autsolpagoid} hover sx={{ bgcolor: d.seleccionado ? 'rgba(254, 243, 199, 0.25)' : 'inherit' }}>
                    <TableCell padding="checkbox" align="center">
                      <Checkbox
                        size="small"
                        checked={Boolean(d.seleccionado)}
                        onChange={() => handleToggleSeleccionDiscrepancia(d.autsolpagoid)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: '#0655A5', fontSize: '0.74rem' }}>
                      {d.oc}
                    </TableCell>
                    <TableCell sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 700 }}>
                      #{d.autsolpagoid}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={d.exhibitActual}
                        size="small"
                        sx={{ fontWeight: 800, fontSize: '0.65rem', bgcolor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, fontSize: '0.74rem', color: '#0f172a' }}>
                      ${Number(d.montoBD || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, fontSize: '0.74rem', color: '#059669' }}>
                      {typeof d.montoEA === 'number'
                        ? `$${d.montoEA.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : d.montoEA}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, fontSize: '0.72rem', color: '#dc2626' }}>
                      {d.diffPesos !== null
                        ? `${d.diffPesos > 0 ? '+' : ''}$${d.diffPesos.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.68rem', color: '#78350f', fontWeight: 600 }}>
                      {d.motivo}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Quitar exhibit de esta solicitud en BD">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={guardandoAuditoria}
                          onClick={() => handleDesvincularDiscrepanciaIndividual(d)}
                          sx={{ p: '3px', borderRadius: '6px', border: '1px solid #fee2e2', bgcolor: '#ffffff', '&:hover': { bgcolor: '#fef2f2' } }}
                        >
                          <DeleteOutline sx={{ fontSize: 16, color: '#dc2626' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 'clamp(10px, 1.2vw, 14px)', bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
            {discrepancias.filter(d => d.seleccionado).length} de {discrepancias.length} seleccionadas para desvincular
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              onClick={() => setModalAuditoriaOpen(false)}
              disabled={guardandoAuditoria}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}
            >
              Cerrar
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={guardandoAuditoria || !discrepancias.some(d => d.seleccionado)}
              onClick={handleDesvincularDiscrepanciasMasivo}
              startIcon={guardandoAuditoria ? <CircularProgress size={13} color="inherit" /> : <DeleteOutline sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.74rem',
                borderRadius: '8px',
                px: 2,
                boxShadow: 'none'
              }}
            >
              {guardandoAuditoria ? 'Desvinculando...' : `Desvincular seleccionadas (${discrepancias.filter(d => d.seleccionado).length})`}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Spinner Premium de Confirmación y Guardado */}
      <SimpleSuccessSpinner
        open={guardando || guardandoAuditoria}
        message={guardandoAuditoria ? 'Desvinculando exhibiciones de la base de datos...' : (progresoMsg || 'Guardando exhibiciones en la base de datos...')}
      />

    </Box>
  );
}
